import type { Deck, Phase, Rating } from "../protocol";
import { deviceId } from "./identity";

const TIMEOUT_MS = 10_000;

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch {
    throw new ApiError("NETWORK", "Can't reach the game server. Check your connection.", 0);
  } finally {
    window.clearTimeout(timer);
  }
  if (res.status === 202 || res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = data?.error;
    throw new ApiError(err?.code ?? "ERROR", err?.message ?? "Something went wrong. Try again.", res.status);
  }
  return data as T;
}

export interface SeatResponse {
  roomCode: string;
  playerId: string;
  playerToken: string;
  lateJoin: boolean;
  wsUrl: string;
}

export interface RoomPreview {
  code: string;
  hostName: string;
  playerCount: number;
  phase: Phase;
  rating: Rating;
  isFull: boolean;
}

export const api = {
  createRoom: (nickname: string, color: string) =>
    request<SeatResponse>("POST", "/v1/rooms", { deviceId: deviceId(), nickname, color }),
  joinRoom: (code: string, nickname: string, color: string) =>
    request<SeatResponse>("POST", `/v1/rooms/${code}/players`, { deviceId: deviceId(), nickname, color }),
  previewRoom: (code: string) => request<RoomPreview>("GET", `/v1/rooms/${code}`),
  decks: () => request<{ decks: Deck[] }>("GET", "/v1/decks"),
  report: (roomCode: string, roundNo: number, reason: string) =>
    request<void>("POST", "/v1/reports", { deviceId: deviceId(), roomCode, roundNo, reason }),
};
